import {Node, Edge} from 'reactflow';
import {IAppliedRule, RuleOperator, RuleResultType} from '../../../../incase-domain/appliedRules';
import {ISlide} from '../../../../incase-domain/slide';
import {ruleEdgeStyleForModifier} from './flow.styles';
import {ActionNodeData, RULE_TYPE_MODIFIER} from '../RulesFlowEditor/nodes.types';
import {ACTION_NODE_X_OFFSET, ACTION_NODE_Y_BASE, ACTION_NODE_Y_STEP, SLIDE_GAP, SLIDE_NODE_WIDTH} from './flow.constants';

export interface ActionNodeCallbacks {
    onChangeType: (type: RuleResultType) => void;
    onAddSlide: (slideId: string) => void;
    onRemoveSlide: (slideId: string) => void;
}

export interface FlowElements {
    actionNodes: Node<ActionNodeData>[];
    predicateEdges: Edge[];
}

export function rulesToFlowElements(
    rules: IAppliedRule[],
    slides: ISlide[],
    makeCallbacks: (nodeId: string) => ActionNodeCallbacks
): FlowElements {
    const actionNodes: Node<ActionNodeData>[] = [];
    const predicateEdges: Edge[] = [];

    const visibleSlides = slides.filter((s) => !s.hidden);
    const ruleCountPerSlide = new Map<number, number>();

    rules.forEach((rule, ruleIndex) => {
        if (!rule.results?.length || !rule.predicates?.length) return;

        const result = rule.results[0];
        if (!result) return;

        const predicate = rule.predicates[0];

        const sourceSlideIndex = visibleSlides.findIndex((slide) => slide.elements?.some((el) => el.id === predicate.elementId));
        const slideIndex = sourceSlideIndex >= 0 ? sourceSlideIndex : 0;

        const slideX = slideIndex * (SLIDE_NODE_WIDTH + SLIDE_GAP);
        const slideRuleIndex = ruleCountPerSlide.get(slideIndex) ?? 0;
        ruleCountPerSlide.set(slideIndex, slideRuleIndex + 1);

        const actionNodeId = `action-rule-${ruleIndex}`;

        const callbacks = makeCallbacks(actionNodeId);

        actionNodes.push({
            id: actionNodeId,
            type: 'action',
            position: {
                x: slideX + ACTION_NODE_X_OFFSET,
                y: ACTION_NODE_Y_BASE + slideRuleIndex * ACTION_NODE_Y_STEP,
            },
            data: {
                ruleType: result.resultType,
                slideIds: result.slideIds,
                operator: predicate.operator ?? RuleOperator.IF,
                ...callbacks,
            },
        });

        const modifier = RULE_TYPE_MODIFIER[result.resultType] ?? 'jump';

        rule.predicates.forEach((pred) => {
            pred.answers?.forEach((answerIndex) => {
                predicateEdges.push({
                    id: `pred-${actionNodeId}-${pred.elementId}-${answerIndex}`,
                    source: `question-${pred.elementId}`,
                    sourceHandle: `answer-${answerIndex}`,
                    target: actionNodeId,
                    targetHandle: 'action-incoming',
                    ...ruleEdgeStyleForModifier(modifier),
                });
            });
        });
    });

    return {actionNodes, predicateEdges};
}
